// TanStack Query hooks for the messaging preferences API.
//
// All keys live in the central QUERY_KEYS registry under the
// ['messaging', ...] namespace so cross-feature invalidations
// (inbox refresh after block/mute) keep working.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { QUERY_KEYS } from '@/lib/query-keys';
import type {
  MessagingPreferenceSummary,
  BlockedUserSummary,
  SenderRole,
  WorkspaceSenderRole,
  ReportCategory,
  UserReportSubmissionResult,
  UserReportSummary,
  MuteDuration,
} from '@tabbio/types';

const KEY_PREFS = QUERY_KEYS.MESSAGING_PREFERENCES;
const KEY_BLOCKED = QUERY_KEYS.MESSAGING_BLOCKED;
const KEY_REPORTS = QUERY_KEYS.MESSAGING_REPORTS;
const KEY_GATE = QUERY_KEYS.MESSAGING_GATED_RECIPIENT;
const KEY_CONV = QUERY_KEYS.MESSAGING_CONVERSATION;

// Tiny helper: collapse APIResponse into data-or-throw
async function unwrap<T>(
  p: Promise<{
    data: T | null;
    error: { code: string; message: string } | null;
  }>,
): Promise<T> {
  const res = await p;
  if (res.error || res.data == null) {
    throw new Error(res.error?.message ?? 'Request failed');
  }
  return res.data;
}

// ─── Preferences ────────────────────────────────────────────────

export function useMessagingPreferences() {
  return useQuery({
    queryKey: KEY_PREFS,
    queryFn: () =>
      unwrap<MessagingPreferenceSummary>(
        api.get<MessagingPreferenceSummary>('/api/messaging/preferences'),
      ),
  });
}

export function useUpdateMessagingPreferences() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      allowedSenderRoles?: SenderRole[];
      allowedWorkspaceSenderRoles?: WorkspaceSenderRole[];
      autoBlockSpam?: boolean;
    }) =>
      unwrap<MessagingPreferenceSummary>(
        api.put<MessagingPreferenceSummary>('/api/messaging/preferences', body),
      ),
    onSuccess: (data) => {
      qc.setQueryData(KEY_PREFS, data);
    },
  });
}

// Optimistic toggle for a single role: used by the prefs screen so the
// switch flips instantly even before the network round-trip resolves.
// The next array is computed exactly once and applied to the cache before
// the PUT: splitting this across onMutate + mutationFn double-toggles
// (onMutate runs first in TanStack v5, so mutationFn would re-read the
// already-flipped cache and send the original value back).
export function useToggleSenderRole(mode: 'seeker' | 'employer') {
  const qc = useQueryClient();
  const update = useUpdateMessagingPreferences();
  return useMutation({
    mutationFn: async (role: SenderRole | WorkspaceSenderRole) => {
      const current = qc.getQueryData<MessagingPreferenceSummary>(KEY_PREFS);
      if (!current) throw new Error('Preferences not loaded yet');
      const patch =
        mode === 'seeker'
          ? {
              allowedSenderRoles: toggleInArray(
                current.allowedSenderRoles,
                role as SenderRole,
              ),
            }
          : {
              allowedWorkspaceSenderRoles: toggleInArray(
                current.allowedWorkspaceSenderRoles,
                role as WorkspaceSenderRole,
              ),
            };
      qc.setQueryData<MessagingPreferenceSummary>(KEY_PREFS, {
        ...current,
        ...patch,
      });
      return update.mutateAsync(patch);
    },
    onError: () => qc.invalidateQueries({ queryKey: KEY_PREFS }),
  });
}

function toggleInArray<T>(arr: T[], v: T): T[] {
  return arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v];
}

// ─── Block ──────────────────────────────────────────────────────

export function useBlockedUsers() {
  return useQuery({
    queryKey: KEY_BLOCKED,
    queryFn: () =>
      unwrap<BlockedUserSummary[]>(
        api.get<BlockedUserSummary[]>('/api/messaging/blocked'),
      ),
  });
}

export function useBlockUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { blockedUserId: string; reason?: string }) =>
      unwrap<{ blocked: true }>(
        api.post<{ blocked: true }>('/api/messaging/block', body),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY_BLOCKED });
      // Conversation list may have changed (some hidden/archived)
      qc.invalidateQueries({ queryKey: QUERY_KEYS.CONVERSATIONS });
    },
  });
}

export function useUnblockUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) =>
      unwrap<{ blocked: false }>(
        api.delete<{ blocked: false }>(`/api/messaging/block/${userId}`),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY_BLOCKED }),
  });
}

// ─── Mute ───────────────────────────────────────────────────────

export function useMuteConversation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (params: { conversationId: string; duration: MuteDuration }) =>
      unwrap<{ muted: true }>(
        api.post<{ muted: true }>(
          `/api/messaging/conversations/${params.conversationId}/mute`,
          { duration: params.duration },
        ),
      ),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: KEY_CONV(vars.conversationId) });
      qc.invalidateQueries({ queryKey: QUERY_KEYS.CONVERSATIONS });
    },
  });
}

export function useUnmuteConversation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (conversationId: string) =>
      unwrap<{ muted: false }>(
        api.delete<{ muted: false }>(
          `/api/messaging/conversations/${conversationId}/mute`,
        ),
      ),
    onSuccess: (_, conversationId) => {
      qc.invalidateQueries({ queryKey: KEY_CONV(conversationId) });
      qc.invalidateQueries({ queryKey: QUERY_KEYS.CONVERSATIONS });
    },
  });
}

// ─── Report ─────────────────────────────────────────────────────

export function useReportUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      reportedUserId: string;
      conversationId?: string;
      category: ReportCategory;
      details?: string;
      clientRequestId?: string;
    }) =>
      unwrap<UserReportSubmissionResult>(
        api.post<UserReportSubmissionResult>('/api/messaging/report', body),
      ),
    onSuccess: ({ report }) => {
      qc.setQueryData<UserReportSummary[]>(KEY_REPORTS, (current = []) => {
        const withoutReplay = current.filter((item) => item.id !== report.id);
        return [report, ...withoutReplay];
      });
    },
  });
}

export function useUserReports() {
  return useQuery({
    queryKey: KEY_REPORTS,
    queryFn: () =>
      unwrap<UserReportSummary[]>(
        api.get<UserReportSummary[]>('/api/messaging/reports'),
      ),
    refetchInterval: (query) =>
      query.state.data?.some(
        (report) =>
          report.status === 'pending' || report.status === 'reviewing',
      )
        ? 30_000
        : false,
  });
}

// ─── Gate cache (read by PreferenceGatedScreen) ─────────────────
// Exported so the createConversation wrapper can prime the cache
// when the server returns RESTRICTED_BY_PREFS.
export const gateRecipientKey = KEY_GATE;
